// Onboarding: the front door. One screen at a time, one big button, and a way
// past every step that is not the login. Everything after the login is a gift,
// not a gate — a step that fails is recorded quietly and we move on, because a
// first run that dead-ends is worse than a first run that learns nothing.
//
// Shape: the welcome screen carries the ember bird and the login together (no
// separate "start" tap), then four numbered steps under a visible "Steg N av 4"
// progress thread — treatment day, height and weight, medicine photos,
// reminders — then the closing screen. A device that already has a session
// skips the welcome and lands straight in step 1.
//
// The closing screen is built ONLY from what actually succeeded. No cheerful
// summary of things that did not happen, no counts we cannot stand behind.

import { getPushKey, postPushSubscribe, streamChat } from "../api";
import { getSession, login } from "../auth";
import { SERVER_URL, getToken } from "../config";
import { clear, el } from "../dom";
import { mountEmber } from "../ember";
import { t } from "../strings";

const ONBOARDED_KEY = "phoenix.onboarded";

// ── small shared helpers ────────────────────────────────────────────────────

/** Stockholm calendar date, matching server/src/core/time.ts. */
function todayStockholm(): string {
  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** Swedish keyboards give a decimal comma; accept both and reject nonsense. */
function readNumber(input: HTMLInputElement, min: number, max: number): number | null {
  const raw = input.value.trim().replace(",", ".");
  if (!raw) return null;
  const value = Number(raw);
  if (!Number.isFinite(value) || value < min || value > max) return null;
  return value;
}

function isIOS(): boolean {
  const ua = navigator.userAgent;
  const ipadOs = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(ua) || ipadOs;
}

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// Same downscale Prata uses (see views/prata.ts): <=1280px JPEG keeps the
// upload small and normalizes HEIC into something the brain accepts.
async function fileToJpeg(file: File): Promise<{ base64: string; dataUrl: string }> {
  const bitmap = await createImageBitmap(file);
  const maxSide = 1280;
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no canvas");
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
  return { base64: dataUrl.slice(dataUrl.indexOf(",") + 1), dataUrl };
}

/** Fire-and-forget JSON call: null on any failure, never throws. */
async function send<T>(path: string, method: string, payload?: unknown): Promise<T | null> {
  try {
    const token = getToken();
    const res = await fetch(`${SERVER_URL}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
    });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

interface CalendarResponse {
  cycle?: { day?: number; ofDays?: number | null } | null;
}

// ── the view ────────────────────────────────────────────────────────────────

export function mountOnboarding(host: HTMLElement): { unmount(): void } {
  let disposed = false;
  // "welcome" (login) -> wizard steps 0..N-1 -> "done". A device that already
  // holds a session has nothing to log into, so it starts inside the wizard.
  let phase: "welcome" | "wizard" | "done" = getSession() ? "wizard" : "welcome";
  let stepIndex = 0;
  let entered = false; // no focus grab on the very first paint
  let ember: { dispose(): void } | null = null;
  const sessionId = crypto.randomUUID();

  // What actually happened. The closing screen reads this and nothing else.
  const saved = { infusion: false, photos: 0, body: false };

  const shell = el("div", { class: "onboard" });
  const top = el("div", { class: "onboard-top" });
  const stage = el("div", { class: "onboard-stage" });
  const foot = el("div", { class: "onboard-foot" });
  shell.append(top, stage, foot);
  clear(host);
  host.append(shell);

  // ── shell helpers ─────────────────────────────────────────────────────────
  function title(text: string): HTMLElement {
    return el("h1", { text, tabindex: "-1" });
  }

  function field(label: string, input: HTMLElement, id: string): HTMLElement {
    input.id = id;
    const wrap = el("div", { class: "field" });
    wrap.append(el("label", { for: id, text: label }), input);
    return wrap;
  }

  function bigBtn(label: string, onClick: () => void): HTMLButtonElement {
    const btn = el("button", { class: "btn primary onboard-cta", type: "button", text: label });
    btn.addEventListener("click", onClick);
    return btn;
  }

  function skipBtn(label: string = t("onboard.skip")): HTMLButtonElement {
    const btn = el("button", { class: "btn quiet onboard-skip", type: "button", text: label });
    btn.addEventListener("click", next);
    return btn;
  }

  function note(text = ""): HTMLElement {
    return el("p", { class: "onboard-note", "aria-live": "polite", text });
  }

  function next(): void {
    if (disposed) return;
    entered = true;
    if (phase === "welcome") phase = "wizard";
    else if (phase === "wizard") {
      if (stepIndex + 1 < STEPS.length) stepIndex += 1;
      else phase = "done";
    }
    render();
  }

  /** Move on either way, but say so when a save did not land. */
  function advance(status: HTMLElement, ok: boolean): void {
    if (disposed) return;
    if (ok) {
      next();
      return;
    }
    status.textContent = t("onboard.failed");
    window.setTimeout(next, 1500);
  }

  function finish(): void {
    localStorage.setItem(ONBOARDED_KEY, "1");
    location.hash = "#/idag";
  }

  // ── välkommen: the bird and the login on one screen ───────────────────────
  function stepWelcome(): void {
    const hero = el("div", { class: "onboard-hero" });
    stage.append(hero);
    stage.append(
      el("h1", { class: "onboard-wordmark", text: t("onboard.welcome.title"), tabindex: "-1" })
    );
    stage.append(el("p", { class: "onboard-lede", text: t("onboard.welcome.tagline") }));
    ember = mountEmber(hero, Math.max(180, Math.min(280, window.innerWidth - 80)));

    const form = el("form", { class: "onboard-form" });
    const email = el("input", {
      type: "email",
      name: "email",
      autocomplete: "username",
      inputmode: "email",
      autocapitalize: "off",
      autocorrect: "off",
      spellcheck: "false",
    });
    const password = el("input", {
      type: "password",
      name: "password",
      autocomplete: "current-password",
    });
    const submit = el("button", {
      class: "btn primary onboard-cta",
      type: "submit",
      text: t("onboard.login.submit"),
    });
    const error = note();
    error.classList.add("err");
    error.hidden = true;
    form.append(
      field(t("onboard.login.email"), email, "onboard-email"),
      field(t("onboard.login.password"), password, "onboard-password"),
      submit
    );
    stage.append(form, error);

    async function attempt(): Promise<void> {
      const address = email.value.trim();
      const secret = password.value;
      if (!address || !secret) return;
      submit.disabled = true;
      submit.textContent = t("onboard.login.working");
      error.hidden = true;
      try {
        await login(address, secret);
        if (disposed) return;
        // A returning device that already finished onboarding goes straight in.
        if (localStorage.getItem(ONBOARDED_KEY) === "1") {
          location.hash = "#/idag";
          unmount();
          return;
        }
        next();
      } catch {
        if (disposed) return;
        error.textContent = navigator.onLine
          ? t("onboard.login.error")
          : t("onboard.login.offline");
        error.hidden = false;
        submit.disabled = false;
        submit.textContent = t("onboard.login.submit");
        password.value = "";
        password.focus();
      }
    }

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      void attempt();
    });
  }

  // ── steg 1: senaste behandlingen ──────────────────────────────────────────
  function stepInfusion(): void {
    stage.append(title(t("onboard.infusion.title")));
    stage.append(el("p", { class: "onboard-sub", text: t("onboard.infusion.hint") }));
    const today = todayStockholm();
    const date = el("input", { type: "date", max: today, class: "onboard-date" });
    date.value = today;
    stage.append(field(t("onboard.infusion.q"), date, "onboard-infusion-date"));
    const status = note();
    stage.append(status);

    const save = bigBtn(t("onboard.infusion.save"), () => {
      const value = date.value;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        next();
        return;
      }
      save.disabled = true;
      void send("/api/calendar", "POST", {
        kind: "infusion",
        date: value,
        title: "Behandling",
      }).then((res) => {
        saved.infusion = res !== null;
        advance(status, saved.infusion);
      });
    });
    foot.append(save, skipBtn(t("onboard.infusion.unknown")));
  }

  // ── steg 2: längd och vikt ────────────────────────────────────────────────
  function stepBody(): void {
    stage.append(title(t("onboard.body.title")));
    stage.append(el("p", { class: "onboard-sub", text: t("onboard.body.hint") }));

    const numeric = (): HTMLInputElement =>
      el("input", { type: "text", inputmode: "decimal", autocomplete: "off", spellcheck: "false" });
    const height = numeric();
    const weight = numeric();
    stage.append(
      field(t("onboard.body.height"), height, "onboard-height"),
      field(t("onboard.body.weight"), weight, "onboard-weight")
    );
    const status = note();
    stage.append(status);

    const save = bigBtn(t("onboard.body.save"), () => {
      const cm = readNumber(height, 100, 250);
      const kg = readNumber(weight, 25, 300);
      if (cm === null && kg === null) {
        next();
        return;
      }
      save.disabled = true;
      void send("/api/patient", "PATCH", {
        ...(cm !== null ? { height_cm: cm } : {}),
        ...(kg !== null ? { weight_ref_kg: kg } : {}),
      }).then((res) => {
        saved.body = res !== null;
        advance(status, saved.body);
      });
    });
    foot.append(save, skipBtn());
  }

  // ── steg 3: fota medicinerna ──────────────────────────────────────────────
  function stepMeds(): void {
    stage.append(title(t("onboard.meds.title")));
    stage.append(el("p", { class: "onboard-sub", text: t("onboard.meds.hint") }));

    const list = el("div", { class: "onboard-list" });
    const review = el("p", { class: "notice small", text: t("onboard.meds.review") });
    review.hidden = true;
    const status = note();
    stage.append(list, review, status);

    const picker = el("input", { type: "file", accept: "image/*", class: "visually-hidden" });
    stage.append(picker);

    const take = bigBtn(t("onboard.meds.take"), () => picker.click());
    const skip = skipBtn();
    const doneBtn = el("button", {
      class: "btn onboard-cta",
      type: "button",
      text: t("onboard.meds.done"),
    });
    doneBtn.addEventListener("click", next);
    doneBtn.hidden = true;
    foot.append(take, doneBtn, skip);

    picker.addEventListener("change", () => {
      const file = picker.files?.[0];
      picker.value = "";
      if (!file) return;
      take.disabled = true;
      status.textContent = t("onboard.meds.reading");

      const row = el("div", { class: "onboard-item" });
      const thumb = el("img", { alt: "" });
      const said = el("span", { class: "onboard-item-text", text: "" });
      row.append(thumb, said);
      list.append(row);

      void (async () => {
        try {
          const { base64, dataUrl } = await fileToJpeg(file);
          thumb.src = dataUrl;
          let reply = "";
          await streamChat(
            {
              message: "Här är mina mediciner.",
              session_id: sessionId,
              image_base64: base64,
              image_media_type: "image/jpeg",
            },
            (piece) => {
              reply += piece;
              said.textContent = reply;
            }
          );
          if (disposed) return;
          if (!reply.trim()) said.textContent = t("onboard.meds.review");
          saved.photos++;
          status.textContent = "";
          review.hidden = false;
          take.textContent = t("onboard.meds.again");
          skip.hidden = true;
          doneBtn.hidden = false;
        } catch {
          if (disposed) return;
          row.remove();
          status.textContent = t("onboard.meds.failed");
        } finally {
          if (!disposed) take.disabled = false;
        }
      })();
    });
  }

  // ── steg 4: påminnelser ───────────────────────────────────────────────────
  function stepPush(): void {
    stage.append(title(t("onboard.push.title")));
    const status = note();

    // iOS Safari only allows Web Push from an installed (standalone) PWA, so on
    // an uninstalled iPhone the honest step is the install instruction instead.
    if (isIOS() && !isStandalone()) {
      const gate = el("div", { class: "notice" });
      gate.append(el("strong", { text: t("onboard.push.ios.title") }), el("br"));
      gate.append(el("span", { text: t("onboard.push.ios.body") }));
      stage.append(gate, status);
      foot.append(bigBtn(t("onboard.push.ios.added"), next), skipBtn(t("onboard.push.later")));
      return;
    }

    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      stage.append(el("div", { class: "notice", text: t("onboard.push.unsupported") }), status);
      foot.append(bigBtn(t("onboard.next"), next));
      return;
    }

    stage.append(el("p", { text: t("onboard.push.q") }));
    stage.append(el("p", { class: "onboard-sub small", text: t("onboard.push.hint") }));
    stage.append(status);

    const yes = bigBtn(t("onboard.push.yes"), () => {
      yes.disabled = true;
      void enable();
    });
    foot.append(yes, skipBtn(t("onboard.push.later")));

    async function enable(): Promise<void> {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          if (disposed) return;
          status.textContent = t("onboard.push.denied");
          window.setTimeout(next, 1600);
          return;
        }
        const reg = await navigator.serviceWorker.register("/sw.js");
        await navigator.serviceWorker.ready;
        const { key } = await getPushKey();
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
        });
        const json = sub.toJSON();
        await postPushSubscribe({
          endpoint: sub.endpoint,
          p256dh: json.keys?.["p256dh"] ?? "",
          auth: json.keys?.["auth"] ?? "",
          ua: navigator.userAgent,
          role: "patient",
        });
        if (disposed) return;
        status.textContent = t("onboard.push.on");
        window.setTimeout(next, 1200);
      } catch {
        if (disposed) return;
        status.textContent = t("onboard.push.error");
        window.setTimeout(next, 1600);
      }
    }
  }

  // ── klart ─────────────────────────────────────────────────────────────────
  function stepDone(): void {
    const hero = el("div", { class: "onboard-hero small" });
    stage.append(hero, title(t("onboard.done.title")));
    ember = mountEmber(hero, 200, true);

    const lines = el("ul", { class: "onboard-summary" });
    stage.append(lines);
    const add = (text: string): void => {
      lines.append(el("li", { text }));
    };

    if (saved.photos === 1) add(t("onboard.done.photos.one"));
    else if (saved.photos > 1) add(`${saved.photos} ${t("onboard.done.photos.many")}`);
    if (saved.body) add(t("onboard.done.body"));
    if (!saved.infusion && !saved.photos && !saved.body) {
      stage.append(el("p", { class: "muted", text: t("onboard.done.nothing") }));
    }

    // The cycle day is the server's to state, so we ask rather than compute it.
    // If the answer does not arrive, the line simply is not there.
    if (saved.infusion) {
      void send<CalendarResponse>("/api/calendar?days=1", "GET").then((res) => {
        if (disposed) return;
        const day = res?.cycle?.day;
        if (typeof day !== "number") return;
        const ofDays = res?.cycle?.ofDays;
        const text =
          typeof ofDays === "number"
            ? `${t("onboard.done.day")} ${day} ${t("onboard.of")} ${ofDays}.`
            : `${t("onboard.done.day")} ${day} ${t("onboard.done.sinceInfusion")}`;
        lines.prepend(el("li", { text }));
      });
    }

    foot.append(bigBtn(t("onboard.done.open"), finish));
  }

  const STEPS: Array<() => void> = [stepInfusion, stepBody, stepMeds, stepPush];

  function render(): void {
    if (disposed) return;
    if (ember) {
      ember.dispose();
      ember = null;
    }
    clear(top);
    clear(stage);
    clear(foot);

    // The progress thread exists only inside the wizard: the welcome screen is
    // a front door, not a step, and "Klart" speaks for itself.
    if (phase === "wizard") {
      const label = `${t("onboard.step")} ${stepIndex + 1} ${t("onboard.of")} ${STEPS.length}`;
      const bar = el("div", { class: "onboard-progress" });
      const fill = el("i");
      fill.style.width = `${((stepIndex + 1) / STEPS.length) * 100}%`;
      bar.append(fill);
      top.append(el("p", { class: "onboard-steplabel", "aria-live": "polite", text: label }), bar);
    }

    if (phase === "welcome") stepWelcome();
    else if (phase === "done") stepDone();
    else STEPS[stepIndex]!();

    if (entered) {
      const heading = stage.querySelector("h1");
      if (heading instanceof HTMLElement) heading.focus();
    }
  }

  function unmount(): void {
    if (disposed) return;
    disposed = true;
    if (ember) {
      ember.dispose();
      ember = null;
    }
    clear(host);
  }

  render();

  return { unmount };
}
